import React, { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Check, CheckCircle2, Circle, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * JourneySections — Phase 3 front-of-funnel editor.
 *
 * A single panel that renders forms for the five brief §6 stages that
 * live BEFORE Application Preparation:
 *   1. Lead — lead_details section
 *   2. Discovery — discovery section
 *   3. Portal & Documents — portal section (with outstanding_docs checklist)
 *   4. Credit Assessment — credit_assessment section
 *   5. Proposal — proposal section
 *
 * Every save is a PATCH on `/api/lender-packs/{id}` with the section
 * sub-object merged. The parent updates the pack from the response so
 * downstream tabs (Journey, Dashboard, Activity Timeline) stay in sync.
 */
export default function JourneySections({ pack, onPackChange }) {
  const { token } = useAuth();
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [saving, setSaving] = useState(null);

  const patch = async (section, incoming) => {
    setSaving(section);
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${pack.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({ [section]: incoming }),
      });
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      onPackChange?.(updated);
      toast.success(`${label(section)} saved`);
    } catch (e) {
      toast.error(`Save failed — ${e.message}`);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="journey-sections">
      <LeadSection pack={pack} onSave={(v) => patch('lead_details', v)} saving={saving === 'lead_details'} />
      <DiscoverySection pack={pack} onSave={(v) => patch('discovery', v)} saving={saving === 'discovery'} />
      <PortalSection pack={pack} auth={auth} onPackChange={onPackChange} onSave={(v) => patch('portal', v)} saving={saving === 'portal'} />
      <CreditSection pack={pack} onSave={(v) => patch('credit_assessment', v)} saving={saving === 'credit_assessment'} />
      <ProposalSection pack={pack} onSave={(v) => patch('proposal', v)} saving={saving === 'proposal'} />
      <SettlementSection pack={pack} onSave={(v) => patch('settlement', v)} saving={saving === 'settlement'} />
    </div>
  );
}

const label = (k) => k.split('_').map((s) => s[0].toUpperCase() + s.slice(1)).join(' ');

const SectionCard = ({ title, subtitle, children, onSave, saving, testid, valid = true }) => (
  <div className="rounded-2xl border bg-white shadow-sm" data-testid={`section-${testid}`}>
    <div className="px-4 py-3 border-b flex items-center justify-between">
      <div>
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        {subtitle ? <div className="text-[11px] text-slate-500">{subtitle}</div> : null}
      </div>
      <Button size="sm" onClick={onSave} disabled={saving || !valid} className="h-8 text-xs bg-slate-900 hover:bg-slate-800" data-testid={`${testid}-save`}>
        {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
        Save
      </Button>
    </div>
    <div className="p-4 space-y-3">{children}</div>
  </div>
);

const Field = ({ label: l, children, className = '' }) => (
  <div className={className}>
    <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">{l}</Label>
    {children}
  </div>
);

// ── 1. LEAD ──────────────────────────────────────────────────────────
function LeadSection({ pack, onSave, saving }) {
  const [f, setF] = useState({
    source: pack.lead_details?.source || '',
    campaign: pack.lead_details?.campaign || '',
    reason_for_enquiry: pack.lead_details?.reason_for_enquiry || '',
    loan_purpose_summary: pack.lead_details?.loan_purpose_summary || '',
    next_action: pack.lead_details?.next_action || '',
    next_action_due_date: pack.lead_details?.next_action_due_date || '',
    follow_up_channel: pack.lead_details?.follow_up_channel || 'phone',
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  return (
    <SectionCard title="Lead" subtitle="How this file came to us and what's next" testid="lead" saving={saving} onSave={() => onSave(f)}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Source"><Input value={f.source} onChange={(e) => set('source', e.target.value)} data-testid="lead-section-source" /></Field>
        <Field label="Campaign"><Input value={f.campaign} onChange={(e) => set('campaign', e.target.value)} data-testid="lead-section-campaign" /></Field>
        <Field label="Reason for enquiry" className="col-span-2"><Input value={f.reason_for_enquiry} onChange={(e) => set('reason_for_enquiry', e.target.value)} data-testid="lead-section-reason" /></Field>
        <Field label="Loan purpose summary" className="col-span-2"><Input value={f.loan_purpose_summary} onChange={(e) => set('loan_purpose_summary', e.target.value)} data-testid="lead-section-purpose" /></Field>
        <Field label="Next action"><Input value={f.next_action} onChange={(e) => set('next_action', e.target.value)} data-testid="lead-section-next-action" /></Field>
        <Field label="Due"><Input type="date" value={(f.next_action_due_date || '').substring(0, 10)} onChange={(e) => set('next_action_due_date', e.target.value)} data-testid="lead-section-due" /></Field>
        <Field label="Follow-up channel">
          <Select value={f.follow_up_channel} onValueChange={(v) => set('follow_up_channel', v)}>
            <SelectTrigger data-testid="lead-section-channel-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="phone">Phone</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="sms">SMS</SelectItem>
              <SelectItem value="in_person">In person</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
    </SectionCard>
  );
}

// ── 2. DISCOVERY ─────────────────────────────────────────────────────
function DiscoverySection({ pack, onSave, saving }) {
  const [f, setF] = useState({
    call_booked_at: pack.discovery?.call_booked_at || '',
    call_completed_at: pack.discovery?.call_completed_at || '',
    goals: pack.discovery?.goals || '',
    current_position: pack.discovery?.current_position || '',
    property_goals: pack.discovery?.property_goals || '',
    timeframe: pack.discovery?.timeframe || '',
    estimated_borrowing: pack.discovery?.estimated_borrowing || '',
    urgency: pack.discovery?.urgency || '',
    initial_servicing_position: pack.discovery?.initial_servicing_position || '',
    risks_concerns: pack.discovery?.risks_concerns || '',
    outcome: pack.discovery?.outcome || '',
    outcome_reason: pack.discovery?.outcome_reason || '',
    next_action: pack.discovery?.next_action || '',
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  return (
    <SectionCard title="Discovery & Qualification" subtitle="What the client wants and whether we can help" testid="discovery" saving={saving} onSave={() => onSave(f)}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Call booked"><Input type="datetime-local" value={f.call_booked_at ? f.call_booked_at.substring(0, 16) : ''} onChange={(e) => set('call_booked_at', e.target.value)} data-testid="discovery-booked" /></Field>
        <Field label="Call completed"><Input type="datetime-local" value={f.call_completed_at ? f.call_completed_at.substring(0, 16) : ''} onChange={(e) => set('call_completed_at', e.target.value)} data-testid="discovery-completed" /></Field>
        <Field label="Goals" className="col-span-2"><Textarea rows={2} value={f.goals} onChange={(e) => set('goals', e.target.value)} data-testid="discovery-goals" /></Field>
        <Field label="Current position"><Textarea rows={2} value={f.current_position} onChange={(e) => set('current_position', e.target.value)} data-testid="discovery-position" /></Field>
        <Field label="Property goals"><Textarea rows={2} value={f.property_goals} onChange={(e) => set('property_goals', e.target.value)} data-testid="discovery-property" /></Field>
        <Field label="Timeframe"><Input value={f.timeframe} onChange={(e) => set('timeframe', e.target.value)} placeholder="e.g. 3 months" data-testid="discovery-timeframe" /></Field>
        <Field label="Estimated borrowing"><Input type="number" value={f.estimated_borrowing} onChange={(e) => set('estimated_borrowing', e.target.value)} placeholder="AUD" data-testid="discovery-borrowing" /></Field>
        <Field label="Urgency"><Input value={f.urgency} onChange={(e) => set('urgency', e.target.value)} data-testid="discovery-urgency" /></Field>
        <Field label="Initial servicing position"><Input value={f.initial_servicing_position} onChange={(e) => set('initial_servicing_position', e.target.value)} data-testid="discovery-servicing" /></Field>
        <Field label="Risks / concerns" className="col-span-2"><Textarea rows={2} value={f.risks_concerns} onChange={(e) => set('risks_concerns', e.target.value)} data-testid="discovery-risks" /></Field>
        <Field label="Outcome">
          <Select value={f.outcome || 'unset'} onValueChange={(v) => set('outcome', v === 'unset' ? '' : v)}>
            <SelectTrigger data-testid="discovery-outcome-trigger"><SelectValue placeholder="Not yet recorded" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unset">— unset —</SelectItem>
              <SelectItem value="qualified">Qualified</SelectItem>
              <SelectItem value="not_qualified">Not qualified</SelectItem>
              <SelectItem value="more_info_required">More info required</SelectItem>
              <SelectItem value="waiting_on_client">Waiting on client</SelectItem>
              <SelectItem value="ready_for_portal">Ready for portal</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Outcome reason"><Input value={f.outcome_reason} onChange={(e) => set('outcome_reason', e.target.value)} data-testid="discovery-outcome-reason" /></Field>
        <Field label="Next action" className="col-span-2"><Input value={f.next_action} onChange={(e) => set('next_action', e.target.value)} data-testid="discovery-next-action" /></Field>
      </div>
    </SectionCard>
  );
}

// ── 3. PORTAL & DOCUMENTS ────────────────────────────────────────────
function PortalSection({ pack, auth, onSave, saving, onPackChange }) {
  const [f, setF] = useState({
    invited_at: pack.portal?.invited_at || '',
    opened_at: pack.portal?.opened_at || '',
    forms_started_at: pack.portal?.forms_started_at || '',
    forms_completed_at: pack.portal?.forms_completed_at || '',
    consent_signed_at: pack.portal?.consent_signed_at || '',
    id_received_at: pack.portal?.id_received_at || '',
    income_docs_at: pack.portal?.income_docs_at || '',
    liabilities_docs_at: pack.portal?.liabilities_docs_at || '',
    bank_statements_at: pack.portal?.bank_statements_at || '',
    frollo_completed_at: pack.portal?.frollo_completed_at || '',
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const docs = pack.portal?.outstanding_docs || [];

  const [newDoc, setNewDoc] = useState({ name: '', responsibility: 'Client', due_by: '' });
  const [busyDoc, setBusyDoc] = useState(false);

  const addDoc = async () => {
    if (!newDoc.name.trim()) { toast.error('Doc name is required'); return; }
    setBusyDoc(true);
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${pack.id}/portal/docs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify(newDoc),
      });
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      onPackChange?.(updated);
      setNewDoc({ name: '', responsibility: 'Client', due_by: '' });
    } catch (e) { toast.error(`Add doc failed — ${e.message}`); }
    finally { setBusyDoc(false); }
  };
  const toggleReceived = async (doc) => {
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${pack.id}/portal/docs/${doc.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({
          name: doc.name,
          received_at: doc.received_at ? null : new Date().toISOString(),
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      onPackChange?.(await res.json());
    } catch (e) { toast.error(`Update failed — ${e.message}`); }
  };
  const removeDoc = async (doc) => {
    if (!confirm(`Remove "${doc.name}"?`)) return;
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${pack.id}/portal/docs/${doc.id}`, {
        method: 'DELETE', headers: auth,
      });
      if (!res.ok) throw new Error(await res.text());
      onPackChange?.(await res.json());
    } catch (e) { toast.error(`Remove failed — ${e.message}`); }
  };

  const outstandingCount = docs.filter((d) => !d.received_at).length;

  return (
    <SectionCard title={`Portal & Documents · ${outstandingCount} outstanding`} subtitle="Client portal milestones and requested documents" testid="portal" saving={saving} onSave={() => onSave(f)}>
      <div className="grid grid-cols-2 gap-3">
        {[
          ['invited_at',        'Portal invited'],
          ['opened_at',         'Portal opened'],
          ['forms_started_at',  'Forms started'],
          ['forms_completed_at','Forms completed'],
          ['consent_signed_at', 'Consent signed'],
          ['id_received_at',    'ID received'],
          ['income_docs_at',    'Income docs received'],
          ['liabilities_docs_at','Liabilities received'],
          ['bank_statements_at','Bank statements received'],
          ['frollo_completed_at','Frollo completed'],
        ].map(([key, lbl]) => (
          <Field key={key} label={lbl}>
            <Input type="datetime-local" value={f[key] ? f[key].substring(0, 16) : ''} onChange={(e) => set(key, e.target.value)} data-testid={`portal-${key}`} />
          </Field>
        ))}
      </div>

      <div className="pt-3 border-t">
        <div className="text-[11px] font-semibold text-slate-700 mb-2 uppercase tracking-wider">Outstanding documents</div>
        {docs.length === 0 ? (
          <div className="text-xs text-slate-400 py-2">Nothing outstanding — add the first one below.</div>
        ) : (
          <ul className="space-y-1 mb-3">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center gap-2 border rounded-md px-2 py-1.5 text-xs" data-testid={`portal-doc-${d.id}`}>
                <button onClick={() => toggleReceived(d)} className="flex items-center gap-1 text-slate-700 hover:text-slate-900" data-testid={`portal-doc-toggle-${d.id}`}>
                  {d.received_at ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-slate-400" />}
                </button>
                <span className={`flex-1 ${d.received_at ? 'line-through text-slate-400' : ''}`}>{d.name}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{d.responsibility || 'Client'}</span>
                {d.due_by ? <span className="text-[10px] text-slate-500">due {d.due_by}</span> : null}
                <button onClick={() => removeDoc(d)} className="text-slate-400 hover:text-rose-600" data-testid={`portal-doc-remove-${d.id}`}><Trash2 className="w-3 h-3" /></button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <Input value={newDoc.name} onChange={(e) => setNewDoc((n) => ({ ...n, name: e.target.value }))} placeholder="e.g. 2 payslips" className="text-xs h-8 flex-1" data-testid="portal-doc-new-name" />
          <Select value={newDoc.responsibility} onValueChange={(v) => setNewDoc((n) => ({ ...n, responsibility: v }))}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="portal-doc-new-resp-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['Client', 'Broker', 'CSM', 'Credit', 'External'].map((r) => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input type="date" value={newDoc.due_by} onChange={(e) => setNewDoc((n) => ({ ...n, due_by: e.target.value }))} className="w-36 text-xs h-8" data-testid="portal-doc-new-due" />
          <Button size="sm" onClick={addDoc} disabled={busyDoc || !newDoc.name.trim()} className="h-8 text-xs" data-testid="portal-doc-new-submit">
            {busyDoc ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
            Add
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}

// ── 4. CREDIT ASSESSMENT ─────────────────────────────────────────────
function CreditSection({ pack, onSave, saving }) {
  const [f, setF] = useState({
    officer_name: pack.credit_assessment?.officer_name || '',
    servicing_completed_at: pack.credit_assessment?.servicing_completed_at || '',
    credit_file_reviewed_at: pack.credit_assessment?.credit_file_reviewed_at || '',
    valuations_ordered_at: pack.credit_assessment?.valuations_ordered_at || '',
    proposed_loan_structure: pack.credit_assessment?.proposed_loan_structure || '',
    proposal_prepared_at: pack.credit_assessment?.proposal_prepared_at || '',
    broker_review_required: !!pack.credit_assessment?.broker_review_required,
    client_clarification_required: !!pack.credit_assessment?.client_clarification_required,
    external_advice_required: !!pack.credit_assessment?.external_advice_required,
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  return (
    <SectionCard title="Credit Assessment & Strategy" subtitle="Servicing, credit file, valuations, proposed structure" testid="credit" saving={saving} onSave={() => onSave(f)}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Credit officer"><Input value={f.officer_name} onChange={(e) => set('officer_name', e.target.value)} data-testid="credit-officer" /></Field>
        <Field label="Servicing completed at"><Input type="datetime-local" value={f.servicing_completed_at ? f.servicing_completed_at.substring(0, 16) : ''} onChange={(e) => set('servicing_completed_at', e.target.value)} data-testid="credit-servicing" /></Field>
        <Field label="Credit file reviewed at"><Input type="datetime-local" value={f.credit_file_reviewed_at ? f.credit_file_reviewed_at.substring(0, 16) : ''} onChange={(e) => set('credit_file_reviewed_at', e.target.value)} data-testid="credit-review" /></Field>
        <Field label="Valuations ordered at"><Input type="datetime-local" value={f.valuations_ordered_at ? f.valuations_ordered_at.substring(0, 16) : ''} onChange={(e) => set('valuations_ordered_at', e.target.value)} data-testid="credit-vals" /></Field>
        <Field label="Proposed loan structure" className="col-span-2"><Textarea rows={2} value={f.proposed_loan_structure} onChange={(e) => set('proposed_loan_structure', e.target.value)} data-testid="credit-structure" /></Field>
        <Field label="Proposal prepared at"><Input type="datetime-local" value={f.proposal_prepared_at ? f.proposal_prepared_at.substring(0, 16) : ''} onChange={(e) => set('proposal_prepared_at', e.target.value)} data-testid="credit-prep" /></Field>
        <div className="flex items-center gap-4 pt-6">
          <BoolPill label="Broker review" value={f.broker_review_required} onToggle={(v) => set('broker_review_required', v)} testid="credit-broker-review" />
          <BoolPill label="Client clarify" value={f.client_clarification_required} onToggle={(v) => set('client_clarification_required', v)} testid="credit-client-clarify" />
          <BoolPill label="External advice" value={f.external_advice_required} onToggle={(v) => set('external_advice_required', v)} testid="credit-external-advice" />
        </div>
      </div>
    </SectionCard>
  );
}

// ── 5. PROPOSAL ──────────────────────────────────────────────────────
function ProposalSection({ pack, onSave, saving }) {
  const [f, setF] = useState({
    completed_at: pack.proposal?.completed_at || '',
    sent_at: pack.proposal?.sent_at || '',
    meeting_booked_for: pack.proposal?.meeting_booked_for || '',
    presented_at: pack.proposal?.presented_at || '',
    lender_selected: pack.proposal?.lender_selected || '',
    structure_selected: pack.proposal?.structure_selected || '',
    client_decision: pack.proposal?.client_decision || '',
    changes_requested: pack.proposal?.changes_requested || '',
    decision_at: pack.proposal?.decision_at || '',
    decision_notes: pack.proposal?.decision_notes || '',
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  return (
    <SectionCard title="Proposal & Client Decision" subtitle="Recommendation, selected lender/structure, and the go/no-go call" testid="proposal" saving={saving} onSave={() => onSave(f)}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Proposal completed at"><Input type="datetime-local" value={f.completed_at ? f.completed_at.substring(0, 16) : ''} onChange={(e) => set('completed_at', e.target.value)} data-testid="proposal-completed" /></Field>
        <Field label="Sent to client"><Input type="datetime-local" value={f.sent_at ? f.sent_at.substring(0, 16) : ''} onChange={(e) => set('sent_at', e.target.value)} data-testid="proposal-sent" /></Field>
        <Field label="Meeting booked for"><Input type="datetime-local" value={f.meeting_booked_for ? f.meeting_booked_for.substring(0, 16) : ''} onChange={(e) => set('meeting_booked_for', e.target.value)} data-testid="proposal-meeting" /></Field>
        <Field label="Presented at"><Input type="datetime-local" value={f.presented_at ? f.presented_at.substring(0, 16) : ''} onChange={(e) => set('presented_at', e.target.value)} data-testid="proposal-presented" /></Field>
        <Field label="Lender selected"><Input value={f.lender_selected} onChange={(e) => set('lender_selected', e.target.value)} data-testid="proposal-lender" /></Field>
        <Field label="Structure selected"><Input value={f.structure_selected} onChange={(e) => set('structure_selected', e.target.value)} data-testid="proposal-structure" /></Field>
        <Field label="Client decision">
          <Select value={f.client_decision || 'unset'} onValueChange={(v) => set('client_decision', v === 'unset' ? '' : v)}>
            <SelectTrigger data-testid="proposal-decision-trigger"><SelectValue placeholder="Not decided" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unset">— unset —</SelectItem>
              <SelectItem value="proceeding">Proceeding</SelectItem>
              <SelectItem value="considering">Considering</SelectItem>
              <SelectItem value="changes_required">Changes required</SelectItem>
              <SelectItem value="not_proceeding">Not proceeding</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Decision at"><Input type="datetime-local" value={f.decision_at ? f.decision_at.substring(0, 16) : ''} onChange={(e) => set('decision_at', e.target.value)} data-testid="proposal-decision-at" /></Field>
        <Field label="Changes requested" className="col-span-2"><Textarea rows={2} value={f.changes_requested} onChange={(e) => set('changes_requested', e.target.value)} data-testid="proposal-changes" /></Field>
        <Field label="Decision notes" className="col-span-2"><Textarea rows={2} value={f.decision_notes} onChange={(e) => set('decision_notes', e.target.value)} data-testid="proposal-notes" /></Field>
      </div>
    </SectionCard>
  );
}

function BoolPill({ label: l, value, onToggle, testid }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(!value)}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] border ${
        value
          ? 'bg-amber-100 border-amber-300 text-amber-900'
          : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
      }`}
    >
      {value ? <Check className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
      {l}
    </button>
  );
}

// ── 6. SETTLEMENT ────────────────────────────────────────────────────
function SettlementSection({ pack, onSave, saving }) {
  const [f, setF] = useState({
    scheduled_for: pack.settlement?.scheduled_for || '',
    completed_at: pack.settlement?.completed_at || '',
    funds_confirmed: !!pack.settlement?.funds_confirmed,
    lender_ready: !!pack.settlement?.lender_ready,
    solicitor_ready: !!pack.settlement?.solicitor_ready,
    discharge_ready: !!pack.settlement?.discharge_ready,
    final_loan_amount: pack.settlement?.final_loan_amount || '',
    broker_commission: pack.settlement?.broker_commission || '',
    client_notified_at: pack.settlement?.client_notified_at || '',
    referral_partner_notified_at: pack.settlement?.referral_partner_notified_at || '',
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  return (
    <SectionCard title="Settlement" subtitle="Bookings, readiness checks, final amounts, and handover triggers" testid="settlement" saving={saving} onSave={() => onSave(f)}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Scheduled for"><Input type="datetime-local" value={f.scheduled_for ? f.scheduled_for.substring(0, 16) : ''} onChange={(e) => set('scheduled_for', e.target.value)} data-testid="settlement-scheduled" /></Field>
        <Field label="Completed at"><Input type="datetime-local" value={f.completed_at ? f.completed_at.substring(0, 16) : ''} onChange={(e) => set('completed_at', e.target.value)} data-testid="settlement-completed" /></Field>
        <Field label="Final loan amount"><Input type="number" value={f.final_loan_amount} onChange={(e) => set('final_loan_amount', e.target.value)} placeholder="AUD" data-testid="settlement-final" /></Field>
        <Field label="Broker commission"><Input type="number" value={f.broker_commission} onChange={(e) => set('broker_commission', e.target.value)} placeholder="AUD" data-testid="settlement-commission" /></Field>
        <Field label="Client notified at"><Input type="datetime-local" value={f.client_notified_at ? f.client_notified_at.substring(0, 16) : ''} onChange={(e) => set('client_notified_at', e.target.value)} data-testid="settlement-client-notified" /></Field>
        <Field label="Referral partner notified at"><Input type="datetime-local" value={f.referral_partner_notified_at ? f.referral_partner_notified_at.substring(0, 16) : ''} onChange={(e) => set('referral_partner_notified_at', e.target.value)} data-testid="settlement-referral-notified" /></Field>
        <div className="col-span-2 flex items-center gap-4 pt-2">
          <BoolPill label="Funds confirmed" value={f.funds_confirmed} onToggle={(v) => set('funds_confirmed', v)} testid="settlement-funds" />
          <BoolPill label="Lender ready" value={f.lender_ready} onToggle={(v) => set('lender_ready', v)} testid="settlement-lender-ready" />
          <BoolPill label="Solicitor ready" value={f.solicitor_ready} onToggle={(v) => set('solicitor_ready', v)} testid="settlement-solicitor-ready" />
          <BoolPill label="Discharge ready" value={f.discharge_ready} onToggle={(v) => set('discharge_ready', v)} testid="settlement-discharge-ready" />
        </div>
      </div>
    </SectionCard>
  );
}
